import { useState, useMemo, useEffect } from "react"
import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import {
  CALCULATOR_DEFAULTS,
  PLANS,
  calculateROI,
  formatCurrency,
} from "@/data/tools/calculator-defaults"
import type { Plan } from "@/data/tools/calculator-defaults"
import { CalculatorResultCard, CalculatorSlider, CalculatorActionsBar } from "@/components/public/calculators"
import { encodeCalculatorParams, decodeCalculatorParams } from "@/lib/calculator-url-params"
import { generateCalculatorPdf } from "@/lib/calculator-pdf"

/* ------------------------------------------------------------------ */
/* FAQ data                                                            */
/* ------------------------------------------------------------------ */

const CALCULATOR_FAQ = [
  {
    q: "Where do the statistics come from?",
    a: "The 73% vendor preference figure comes from industry research by the National Association of Estate Agents. The 2x enquiry uplift is based on aggregated data from agents using video marketing on Rightmove and Zoopla. The 40% figure comes from vendor surveys on what influenced their choice of agent.",
  },
  {
    q: "Is three extra instructions per year realistic?",
    a: "It's conservative. Agents who use video at market appraisals consistently report higher conversion rates. If you're attending 20-30 appraisals a year and converting an extra 10-15% because of video, that's 2-5 additional instructions. We use 3 as a sensible starting point.",
  },
  {
    q: "What counts as an extra instruction?",
    a: "Any instruction you win that you wouldn't have won without video. It could be a vendor who chose you over a competitor because of your marketing materials, or a referral that came because a previous vendor was impressed by their listing video. If video tipped the decision, it counts.",
  },
  {
    q: "What if my average property price is different?",
    a: "Use the slider to match your market. The maths works at any price point: whether you're selling studio flats at £150,000 or family homes at £750,000, the principle is the same. One extra instruction covers the annual cost of QuiteRight many times over.",
  },
  {
    q: "How does video help win instructions at market appraisals?",
    a: "When you present a market appraisal, showing examples of your listing videos immediately differentiates you from competitors who rely on photos alone. Vendors can see exactly how their property will be marketed, and the quality of the video signals a professional, modern agency. It builds confidence and makes your pitch more memorable.",
  },
  {
    q: "Which QuiteRight plan should I choose?",
    a: "It depends on your volume. The Starter plan (£49/month for 20 videos) suits smaller independent agencies. The Managed plan (£299/month for 50 videos) is the most popular and offers the best per-video value for mid-size agencies. The Agency plan (£599/month for 150 videos) is designed for multi-branch operations with high listing volumes.",
  },
  {
    q: "What if I do not win any extra instructions?",
    a: "Even without additional instructions, video adds value to your existing listings by generating more enquiries, faster viewings, and stronger social media engagement. However, the industry data strongly suggests that agencies using video at appraisals do win more instructions. The break-even point shown in the calculator is typically just one or two per year.",
  },
  {
    q: "Does this calculator account for VAT?",
    a: "The QuiteRight plan costs shown include VAT. The fee revenue figures are based on your gross commission before any VAT or other deductions. For a precise comparison, adjust the average property price and fee percentage to reflect your net figures if preferred.",
  },
  {
    q: "How quickly can I expect to see results?",
    a: "Most agents report an impact within the first few market appraisals where they use video. The conversion lift tends to be immediate because the differentiator is visible during the presentation itself. Over time, the effect compounds as your social media presence grows and vendors begin approaching you because they have seen your video content online.",
  },
  {
    q: "Can I use this calculator for lettings as well?",
    a: "Yes. Replace the average property price with your average annual rent, and the fee percentage with your lettings management fee. The principle is identical: if video helps you win even one extra landlord instruction per year, it more than covers the cost of the platform. Many letting agents find video particularly effective for showcasing rental properties on social media.",
  },
]

/* ------------------------------------------------------------------ */
/* Bar chart                                                           */
/* ------------------------------------------------------------------ */

function InstructionBarChart({
  feePerInstruction,
  annualCost,
  extraInstructions,
}: {
  feePerInstruction: number
  annualCost: number
  extraInstructions: number
}) {
  const bars = Array.from({ length: Math.min(extraInstructions + 2, 8) }, (_, i) => {
    const count = i + 1
    return {
      label: `+${count}`,
      revenue: feePerInstruction * count,
      active: count <= extraInstructions,
    }
  })

  const maxRevenue = Math.max(...bars.map((b) => b.revenue), annualCost)
  const chartHeight = 160

  return (
    <div className="rounded-xl border border-stone bg-white p-6">
      <p className="text-xs font-semibold text-charcoal">
        Revenue per extra instruction
      </p>

      <div className="relative mt-4">
        <div className="flex items-end gap-2" style={{ height: chartHeight }}>
          {bars.map((bar) => {
            const barPx = maxRevenue > 0 ? (bar.revenue / maxRevenue) * chartHeight : 0
            return (
              <div
                key={bar.label}
                className={`flex-1 rounded-t-md transition-all duration-300 ${
                  bar.active ? "bg-red" : "bg-red/20"
                }`}
                style={{ height: Math.max(barPx, 4) }}
              />
            )
          })}
        </div>

        {/* Cost line */}
        {annualCost > 0 && maxRevenue > 0 && (
          <div
            className="pointer-events-none absolute left-0 right-0 border-t-2 border-dashed border-charcoal/40"
            style={{ bottom: (annualCost / maxRevenue) * chartHeight }}
          />
        )}
      </div>

      {/* Bar labels */}
      <div className="mt-1.5 flex gap-2">
        {bars.map((bar) => (
          <div key={bar.label} className="flex-1 text-center">
            <p className="text-[10px] font-semibold text-charcoal">
              {formatCurrency(bar.revenue)}
            </p>
            <p className="text-[10px] font-medium text-warm-grey">{bar.label}</p>
          </div>
        ))}
      </div>

      {/* Cost legend */}
      {annualCost > 0 && maxRevenue > 0 && (
        <div className="mt-3 flex items-center justify-end gap-1.5">
          <div className="size-2 rounded-full bg-charcoal/40" />
          <span className="text-[10px] font-medium text-warm-grey">
            Annual cost: {formatCurrency(annualCost)}
          </span>
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Page component                                                      */
/* ------------------------------------------------------------------ */

export default function InstructionCalculatorPage({ meta }: { meta: SeoMeta }) {
  const initialParams = useMemo(() => {
    if (typeof window === "undefined") return { avgPropertyPrice: CALCULATOR_DEFAULTS.avgPropertyPrice, avgFeePercent: CALCULATOR_DEFAULTS.avgFeePercent, extraInstructionsPerYear: CALCULATOR_DEFAULTS.extraInstructionsPerYear, plan: PLANS[1].name as string }
    const sp = new URLSearchParams(window.location.search)
    return decodeCalculatorParams(
      { avgPropertyPrice: CALCULATOR_DEFAULTS.avgPropertyPrice, avgFeePercent: CALCULATOR_DEFAULTS.avgFeePercent, extraInstructionsPerYear: CALCULATOR_DEFAULTS.extraInstructionsPerYear, plan: PLANS[1].name as string },
      sp,
    )
  }, [])

  const [avgPrice, setAvgPrice] = useState<number>(initialParams.avgPropertyPrice as number)
  const [feePercent, setFeePercent] = useState<number>(initialParams.avgFeePercent as number)
  const [extraInstructions, setExtraInstructions] = useState<number>(initialParams.extraInstructionsPerYear as number)
  const [selectedPlan, setSelectedPlan] = useState<Plan>(
    PLANS.find((p) => p.name === initialParams.plan) ?? PLANS[1],
  )

  const results = calculateROI({
    avgPropertyPrice: avgPrice,
    feePercent,
    extraInstructions,
    monthlyCost: selectedPlan.monthlyCost,
  })

  useEffect(() => {
    const params = encodeCalculatorParams({
      avgPropertyPrice: avgPrice, avgFeePercent: feePercent,
      extraInstructionsPerYear: extraInstructions, plan: selectedPlan.name,
    })
    window.history.replaceState(null, "", `?${params}`)
  }, [avgPrice, feePercent, extraInstructions, selectedPlan])

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}?${encodeCalculatorParams({ avgPropertyPrice: avgPrice, avgFeePercent: feePercent, extraInstructionsPerYear: extraInstructions, plan: selectedPlan.name })}`
      : ""
  const shareTitle = `Net profit from video: ${results.netProfit >= 0 ? "+" : ""}${formatCurrency(results.netProfit)}/year on the ${selectedPlan.name} plan`

  const handleDownloadPdf = () =>
    generateCalculatorPdf({
      title: "Instruction Revenue Calculation",
      subtitle: `ROI analysis for video marketing on the ${selectedPlan.name} plan`,
      sections: [
        {
          title: "Results",
          rows: [
            { label: "Net profit per year", value: `${results.netProfit >= 0 ? "+" : ""}${formatCurrency(results.netProfit)}`, sub: `${results.roi >= 0 ? "+" : ""}${results.roi.toFixed(0)}% return on investment`, accent: true },
            { label: "Fee per instruction", value: formatCurrency(results.feePerInstruction), sub: `${feePercent.toFixed(1)}% on ${formatCurrency(avgPrice)}` },
            { label: "Extra revenue per year", value: formatCurrency(results.annualExtraRevenue), sub: `${extraInstructions} extra instruction${extraInstructions === 1 ? "" : "s"}` },
            { label: "Annual QuiteRight cost", value: formatCurrency(results.annualCost), sub: `${selectedPlan.name} plan` },
            { label: "Break-even point", value: `${results.breakEvenInstructions} instructions/year` },
          ],
        },
      ],
      url: shareUrl,
      generatedAt: new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }),
    })

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />

        {/* Structured Data — WebApplication */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "Instruction Revenue Calculator",
            description: meta.description,
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "GBP",
            },
            provider: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://quiteright.co.uk",
            },
          })}
        </script>

        {/* Structured Data — FAQPage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: CALCULATOR_FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>
        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "Tools", path: "/tools" }, { name: "Instruction Calculator" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Calculator
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            How much is one extra instruction worth<span className="text-red">?</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Enter your numbers and see how quickly video marketing pays for itself.
            Most agents break even with just one or two extra instructions per year.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="border-t border-stone bg-white px-6 py-16">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col gap-10 lg:flex-row lg:gap-16">
            {/* Input panel */}
            <div className="flex-1 space-y-8">
              <div>
                <h2 className="font-display text-xl font-bold text-charcoal">
                  Your numbers<span className="text-red">.</span>
                </h2>
                <p className="mt-1 text-sm text-slate">
                  Adjust the sliders to match your agency.
                </p>
              </div>

              <CalculatorSlider
                label="Average property sale price"
                value={avgPrice}
                min={100_000}
                max={2_000_000}
                step={25_000}
                formatValue={(v) => formatCurrency(v)}
                onChange={setAvgPrice}
              />

              <CalculatorSlider
                label="Your fee percentage"
                value={feePercent}
                min={0.5}
                max={3}
                step={0.1}
                formatValue={(v) => `${v.toFixed(1)}%`}
                onChange={setFeePercent}
              />

              <CalculatorSlider
                label="Extra instructions per year with video"
                value={extraInstructions}
                min={1}
                max={20}
                step={1}
                formatValue={(v) => `${v}`}
                onChange={setExtraInstructions}
              />

              {/* Plan selector */}
              <div>
                <label className="text-sm font-medium text-charcoal">
                  QuiteRight plan
                </label>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {PLANS.map((plan) => (
                    <button
                      key={plan.name}
                      type="button"
                      onClick={() => setSelectedPlan(plan)}
                      className={`rounded-lg border px-3 py-3 text-center transition-all ${
                        selectedPlan.name === plan.name
                          ? "border-red bg-red/5 ring-1 ring-red/20"
                          : "border-stone bg-white hover:border-stone/80"
                      }`}
                    >
                      <p
                        className={`text-sm font-semibold ${
                          selectedPlan.name === plan.name
                            ? "text-red"
                            : "text-charcoal"
                        }`}
                      >
                        {plan.name}
                      </p>
                      <p className="mt-0.5 text-xs text-warm-grey">
                        £{plan.monthlyCost}/mo
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Results panel */}
            <div className="flex-1 space-y-4">
              <div>
                <h2 className="font-display text-xl font-bold text-charcoal">
                  Your return<span className="text-red">.</span>
                </h2>
                <p className="mt-1 text-sm text-slate">
                  Based on your numbers, here&rsquo;s what video marketing is worth.
                </p>
              </div>

              <CalculatorActionsBar shareUrl={shareUrl} shareTitle={shareTitle} onDownloadPdf={handleDownloadPdf} />

              {/* Net profit — hero metric */}
              <CalculatorResultCard
                label="Net profit per year"
                value={`${results.netProfit >= 0 ? "+" : ""}${formatCurrency(results.netProfit)}`}
                sub={`${results.roi >= 0 ? "+" : ""}${results.roi.toFixed(0)}% return on investment`}
                accent
                large
              />

              <div className="grid grid-cols-2 gap-4">
                <CalculatorResultCard
                  label="Fee per instruction"
                  value={formatCurrency(results.feePerInstruction)}
                  sub={`${feePercent.toFixed(1)}% on ${formatCurrency(avgPrice)}`}
                />
                <CalculatorResultCard
                  label="Extra revenue per year"
                  value={formatCurrency(results.annualExtraRevenue)}
                  sub={`${extraInstructions} extra instruction${extraInstructions === 1 ? "" : "s"}`}
                />
                <CalculatorResultCard
                  label="Annual QuiteRight cost"
                  value={formatCurrency(results.annualCost)}
                  sub={`${selectedPlan.name} plan, £${selectedPlan.monthlyCost}/mo`}
                />
                <CalculatorResultCard
                  label="Break-even point"
                  value={`${results.breakEvenInstructions}`}
                  sub="instructions per year"
                />
              </div>

              {/* Chart */}
              <InstructionBarChart
                feePerInstruction={results.feePerInstruction}
                annualCost={results.annualCost}
                extraInstructions={extraInstructions}
              />
            </div>
          </div>
        </div>
      </section>

      {/* How we calculate */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            How we calculate this<span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              The calculation is straightforward: your average sale price multiplied
              by your fee percentage gives the revenue per instruction. Multiply by
              the number of extra instructions you expect to win with video, subtract
              the annual cost of your QuiteRight plan, and you have your net profit.
            </p>
            <p>
              We don&rsquo;t inflate the numbers. The default of three extra
              instructions per year is conservative; many agents report winning
              significantly more once they start showing videos at market appraisals.
              The break-even point tells you exactly how many you need to cover the
              cost.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal stagger className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4">
          {[
            {
              stat: "73%",
              label: "of vendors prefer the agent who offers video",
            },
            {
              stat: "2x",
              label: "more enquiries on listings with video",
            },
            {
              stat: "40%",
              label: "of vendors say video influenced their agent choice",
            },
            {
              stat: "<60s",
              label: "to create a listing video with QuiteRight",
            },
          ].map((item) => (
            <div
              key={item.stat}
              className="bg-charcoal px-5 py-6 text-center"
            >
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">
                {item.label}
              </p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 divide-y divide-stone">
            {CALCULATOR_FAQ.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
                  {item.q}
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  {item.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            Ready to see the impact<span className="text-red">?</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            The numbers speak for themselves. Start your free trial and see what
            video does for your next market appraisal.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/sign-up"
              className="inline-flex w-full items-center justify-center rounded-md bg-red px-8 py-4 text-lg font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Start your free trial
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-md border border-slate bg-charcoal px-8 py-4 text-lg font-medium text-cream transition-colors hover:bg-slate sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
          <p className="mt-5 text-sm text-warm-grey">
            14-day free trial &middot; No credit card &middot; Cancel any time
          </p>
        </div>
      </section>
    </PublicLayout>
  )
}
